<template>
    <div>
        <pc-button
            v-if="canEditDataFields"
            primary
            v-on:click="showCreateEditModal('')">
            {{ createNewButtonLabel }}
        </pc-button>
        <p class="mt-3">
            {{ tvMessage }}
        </p>
        <p>
            {{ typesOfTVRMessage }}
        </p>
        <ul>
            <li>
                {{ characterSetDescription }}
            </li>
            <li>
                {{ patternMatchDescription }}
            </li>
        </ul>
        <pc-loader
            v-bind:loading="loadingTVRs"
            v-bind:message="loadingTextValidationRules">
            <div v-if="!loadingTVRs">
                <pc-table
                    key-prop="PublicId"
                    v-bind:items-per-page="20"
                    v-bind:columns="columns"
                    v-bind:items="tvrItems"
                    v-bind:empty-text="emptyTVRTableMessage">
                    <template v-slot:TextValidationRuleTypeId="textValidationRule">
                        {{ getTVRTypeName(textValidationRule.TextValidationRuleTypeId) }}
                    </template>
                    <template v-slot:DataFieldReferences="textValidationRule">
                        <pc-expand-list
                            v-if="hasDataFieldReferences(textValidationRule)"
                            v-bind:items="getDataFieldNames(textValidationRule)"
                            use-bullets>
                        </pc-expand-list>
                    </template>
                    <template v-slot:actionsRow="textValidationRule">
                        <action-row
                            v-bind:can-edit-data-fields="canEditDataFields"
                            v-bind:text-validation-rule="textValidationRule"
                            v-on:edit="showCreateEditModal(textValidationRule.PublicId)"
                            v-on:copy="copyTVR"
                            v-on:delete="deleteTVR" />
                    </template>
                </pc-table>
            </div>
        </pc-loader>
    </div>
</template>
<script>
    import { mapI18n } from "Util/i18n";
    import { NAMESPACE, KEYS } from "./i18n/textValidationRulesTab.i18n";
    import actionRow from "./textValidationRulesActionRow.vue";
    import editModal, { EDIT_TVR_RESULT } from "./textValidationRulesEditModal.vue";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";
    import { getClientTimestampTableColumn } from "Util/clientDateTime";
    import { DIALOG_HOOK } from "Components/pc-dialog.vue";
    import referencesError from "ProgramAdmin/Components/referencesError.vue";

    export const TEXT_VALIDATION_RULE_TYPES = {
        CHAR_SET: 1,
        PATTERN_MATCH: 2
    };

    export const TEXT_VALIDATION_RULE_CHAR_SETS = {
        A_Z_LOWER: 1,
        A_Z_UPPER: 2,
        NUMERIC: 3,
        PUNCTUATION: 4
    };

    export default {
        components: {
            actionRow
        },
        props: {
            controller: { type: Object, required: true },
            allowedPunctuationSet: { type: String, required: false, default: "" },
            canEditDataFields: { type: Boolean, required: false, default: false },
            textValidationRules: { type: Array, required: true }
        },
        emits: ["load-text-validation-rules", "reload-data-fields"],
        data() {
            return {
                loadingTVRs: false
            };
        },
        computed: {
            columns() {
                return [
                    {
                        header: this.nameColumnHeader,
                        property: "DisplayName",
                        searchable: true,
                        orderable: true,
                        visible: true
                    },
                    {
                        header: this.ruleTypeColumnHeader,
                        property: "TextValidationRuleTypeId",
                        searchable: true,
                        orderable: true,
                        visible: true,
                        searchValue: function (tvr) {
                            return this.getTVRTypeName(tvr.TextValidationRuleTypeId);
                        }.bind(this)
                    },
                    getClientTimestampTableColumn(this.lastModifiedColumnHeader, "LastModifiedTimestamp"),
                    {
                        header: this.referencedByDataFields,
                        property: "DataFieldReferences",
                        searchable: true,
                        orderable: false,
                        visible: true,
                        searchValue: function (tvr) {
                            if (tvr.DataFieldReferences && tvr.DataFieldReferences.length > 0) {
                                return tvr.DataFieldReferences.map((df) => df.Name).join(" ");
                            }
                            return "";
                        }
                    }
                ];
            },
            textValidationRulesMap() {
                const textValidationRulesMap = {};

                if (this.textValidationRules) {
                    for (const tvr of this.textValidationRules) {
                        textValidationRulesMap[tvr.PublicId] = tvr;
                    }
                }

                return textValidationRulesMap;
            },
            tvrItems() {
                return Object.values(this.textValidationRulesMap);
            },
            ...mapI18n(KEYS, NAMESPACE),
            ...mapI18n(COMMON_KEYS, COMMON_NAMESPACE)
        },
        methods: {
            async reloadTextValidationRules() {
                this.loadingTVRs = true;
                await this.$emit("load-text-validation-rules");
                this.loadingTVRs = false;
            },
            async showCreateEditModal(publicId) {
                let tvr = null;
                if (this.textValidationRulesMap.hasOwnProperty(publicId)) {
                    tvr = this.textValidationRulesMap[publicId];
                }

                const dialogResult = await this.$app.showDialog({
                    title: tvr ? this.editTVRModalHeader : this.createNewTVRModalHeader,
                    customComponent: editModal,
                    customComponentOptions: {
                        tvr: tvr,
                        allowedPunctuationSet: this.allowedPunctuationSet
                    },
                    customComponentIsValidToCloseHookAsync: DIALOG_HOOK.isValidToClose,
                    customComponentResultHook: DIALOG_HOOK.getResult,
                    buttons: [
                        {
                            id: "TVRCancelButton",
                            text: this.cancelText,
                            result: EDIT_TVR_RESULT.cancel
                        },
                        {
                            id: "TVRSaveButton",
                            text: this.saveText,
                            primary: true,
                            result: EDIT_TVR_RESULT.save
                        }
                    ]
                });

                if (dialogResult && dialogResult.action === EDIT_TVR_RESULT.save) {
                    this.$app.blockUi(this.savingTVRMessage);
                    let saveResult = null;
                    try {
                        saveResult = await this.controller.SaveTextValidationRule({
                            programId: this.$app.ProgramId,
                            clientTVR: dialogResult.data.textValidationRule
                        });
                    } finally {
                        this.$app.unBlockUi();
                    }

                    if (saveResult.Data.Success) {
                        this.reloadTextValidationRules();
                        this.$emit("reload-data-fields");
                    }
                }
            },
            getDataFieldNames(textValidationRule) {
                if (textValidationRule.DataFieldReferences !== null) {
                    return textValidationRule.DataFieldReferences.map((df) => df.Name);
                }
            },
            hasDataFieldReferences(textValidationRule) {
                return (
                    textValidationRule.DataFieldReferences !== null && textValidationRule.DataFieldReferences.length > 0
                );
            },
            async deleteTVR(publicId) {
                this.$app.blockUi(this.deletingTVRMessage);
                let result = null;

                try {
                    result = await this.controller.DeleteTextValidationRule({
                        programId: this.$app.ProgramId,
                        textValidationRulePublicId: publicId
                    });
                } finally {
                    this.$app.unBlockUi();
                }

                if (result.Data) {
                    if (result.Data.Success) {
                        this.reloadTextValidationRules();
                    } else if (!result.Data.Success && result.Data.ReferencesErrorModel) {
                        this.$app.showDialog({
                            title: this.textValidationRuleStillHasReferencesDialogTitle,
                            customComponent: referencesError,
                            customComponentOptions: {
                                referencesErrorModel: result.Data.ReferencesErrorModel,
                                useErrorStyle: true,
                                refsTitle: this.referencesTitle
                            },
                            buttons: [
                                {
                                    text: this.okText,
                                    primary: true
                                }
                            ]
                        });
                    }
                }
            },
            async copyTVR(publicId) {
                this.$app.blockUi(this.copyingTVRMessage);
                let result = null;
                try {
                    result = await this.controller.CopyTextValidationRule({
                        programId: this.$app.ProgramId,
                        textValidationRulePublicId: publicId
                    });
                } finally {
                    this.$app.unBlockUi();
                }

                if (result.Data && result.Data.Success) {
                    this.reloadTextValidationRules();
                }
            },
            getTVRTypeName(typeId) {
                switch (typeId) {
                    case TEXT_VALIDATION_RULE_TYPES.CHAR_SET:
                        return this.characterSetTypeName;
                    case TEXT_VALIDATION_RULE_TYPES.PATTERN_MATCH:
                        return this.patternMatchTypeName;
                    default:
                        return "";
                }
            }
        }
    };
</script>
